/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { EntityVersion, ID } from '@gh/common-shared-data';

import {
  AbstractRestService,
  arrayOf,
  identity,
  none,
  RestSettings,
  SlicedDataSource,
} from '@gh/core-data';
import { parametrizeUrl } from '@gh/core-util';

import { Observable } from 'rxjs';

import { ScheduleDetailsDto } from '../schedule-templates/schedule-template.dto';
import {
  SalesScheduleFilterDto,
  SalesScheduleListItemDto,
  ScheduleTaskDetailsDto,
} from './schedule.dto';
import {
  SalesScheduleListItemMapper,
  ScheduleDetailsMapper,
  ScheduleTaskDetailsMapper,
} from './schedule.mapper';
import { Confirmation } from '@gh/core-ui';

@Injectable()
export class SalesScheduleRestService extends AbstractRestService {
  constructor(private settings: RestSettings) {
    super(settings);
  }

  findAllAsDataSource(filter: SalesScheduleFilterDto): SlicedDataSource<SalesScheduleListItemDto> {
    return this.createSlicedDataSourceGet(
      `${this.bp}/schedules/sales`,
      filter,
      identity(),
      arrayOf(SalesScheduleListItemMapper));
  }

  findAllAsFinderDataSource(filter: SalesScheduleFilterDto): SlicedDataSource<SalesScheduleListItemDto> {
    return this.createSlicedDataSourceGet(
      `${this.bp}/schedules/sales/finder`,
      filter,
      identity(),
      arrayOf(SalesScheduleListItemMapper));
  }

  remove(scheduleId: ID, version: EntityVersion): Observable<void> {
    return this.httpDelete(
      parametrizeUrl(`${this.bp}/schedules/sales/${scheduleId}`, { version }),
      void 0,
      none(),
      none());
  }

  init(salesFolderId: ID, templateId: ID): Observable<ScheduleDetailsDto> {
    return this.httpGet(`${this.bp}/schedules/sales/init`, { salesFolderId, templateId }, identity(), ScheduleDetailsMapper);
  }

  get(salesFolderId: ID): Observable<ScheduleDetailsDto> {
    return this.httpGet(`${this.bp}/schedules/sales/${salesFolderId}`, ScheduleDetailsMapper);
  }

  getCombined(jobIds: ID[]): Observable<ScheduleDetailsDto[]> {
    return this.httpGet(
      parametrizeUrl(`${this.bp}/schedules/sales/combined-gantt-chart`, { id: jobIds }),
      arrayOf(ScheduleDetailsMapper));
  }

  create(schedule: ScheduleDetailsDto, saveBaseDatesAnswer?: Confirmation): Observable<ScheduleDetailsDto> {
    let url = `${this.bp}/schedules/sales`;
    if (saveBaseDatesAnswer) {
      url = `${url}?saveBaseDates=${saveBaseDatesAnswer === Confirmation.Yes ? 'true' : 'false'}`;
    }
    return this.httpPost(url, schedule, ScheduleDetailsMapper);
  }

  update(schedule: ScheduleDetailsDto, saveBaseDatesAnswer?: Confirmation): Observable<ScheduleDetailsDto> {
    let url = `${this.bp}/schedules/sales/${schedule.scheduleId}`;
    if (saveBaseDatesAnswer) {
      url = `${url}?saveBaseDates=${saveBaseDatesAnswer === Confirmation.Yes ? 'true' : 'false'}`;
    }
    return this.httpPut(url, schedule, ScheduleDetailsMapper);
  }

  recalculate(schedule: ScheduleDetailsDto): Observable<ScheduleDetailsDto> {
    return this.httpPut(`${this.bp}/schedules/sales/recalculate`, schedule, ScheduleDetailsMapper);
  }

  getTask(scheduleId: ID, taskId: ID): Observable<ScheduleTaskDetailsDto> {
    return this.httpGet(`${this.bp}/schedules/sales/${scheduleId}/tasks/${taskId}`, ScheduleTaskDetailsMapper);
  }

  updateTask(scheduleId: ID, taskId: ID, task: ScheduleTaskDetailsDto, saveBaseDatesAnswer?: Confirmation):
    Observable<ScheduleTaskDetailsDto> {
    let url = `${this.bp}/schedules/sales/${scheduleId}/tasks/${taskId}`;
    if (saveBaseDatesAnswer) {
      url = `${url}?saveBaseDates=${saveBaseDatesAnswer === Confirmation.Yes ? 'true' : 'false'}`;
    }
    return this.httpPut(url, task, ScheduleTaskDetailsMapper);
  }
}
